import {
  BOT_SEQUENCE_BODY_LOCALES,
  BOT_SEQUENCE_CONTENT_TEMPLATES,
  BOT_SEQUENCE_DEFAULT_BODY_LOCALE,
  BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG,
  BOT_SEQUENCE_DELIVERY_CHANNELS,
  BOT_SEQUENCE_STEP_LIMITS,
  BotSequenceConfigEffectiveField,
  BotSequenceConfigFieldSource,
  BotSequenceConfigStep,
  BotSequenceConfigStepDelivery,
  BotSequenceConfigValues,
  BotSequenceMode,
  BotSequenceType,
  Channel
} from "@swivl-mono/types";

import { extractBraceVarRefs, findBraceVarSyntaxErrors, isResolvableVarKey } from "../Variables/BraceVars.Helper";

/**
 * The pure SequenceConfig resolution + validation shared by the engine (bull's
 * `BotSequenceConfigService`) and the management API (server's `SequenceConfigService`),
 * so the value the editor previews is by construction the value the scheduler runs.
 *
 * Resolution is FIELD-BY-FIELD, location → group → org, first non-null wins (the
 * row-level "most specific row wins entirely" shape is the trap the escalation-recipient
 * resolver hit). The one exception: the LADDER cascades as one unit — a scope inherits
 * the whole ladder or defines its own, never merged day by day — and an empty array
 * never shadows a populated scope above it (docs/snowy/plans/2026-08-03-sequence-config-object.md).
 *
 * Send HOURS are not resolved here: the config only names a schedule slug
 * (`scheduleSlug`, default "sequence"); the Schedule system owns the hours and closures.
 */

/** The three scope rows that may exist for one (location, sequence type) — any may be absent. */
export interface BotSequenceConfigScopeRows {
  location?: BotSequenceConfigValues | null;
  group?: BotSequenceConfigValues | null;
  org?: BotSequenceConfigValues | null;
}

/** The resolved fields with provenance, before any engine defaults are applied. */
export interface ResolvedBotSequenceConfigValues {
  mode: BotSequenceConfigEffectiveField<BotSequenceMode | null>;
  ladder: BotSequenceConfigEffectiveField<BotSequenceConfigStep[] | null>;
  scheduleSlug: BotSequenceConfigEffectiveField<string>;
  minGapDays: BotSequenceConfigEffectiveField<number>;
  /** null = no scope expressed a preference — the engine falls back to the PROD-559 location variable. */
  pastDueInactiveOptInStatuses: BotSequenceConfigEffectiveField<string[] | null>;
}

// NOTE: enum members are read inside functions only, never at module level — the
// types ↔ helpers package cycle means this module can initialize before the enums do.
const SCOPE_ORDER: readonly ["location", "group", "org"] = ["location", "group", "org"];

function resolveField<K extends keyof BotSequenceConfigValues>(
  rows: BotSequenceConfigScopeRows,
  field: K
): { value: NonNullable<BotSequenceConfigValues[K]> | null; source: BotSequenceConfigFieldSource } {
  for (const scope of SCOPE_ORDER) {
    const value = rows[scope]?.[field];
    if (value === null || value === undefined) {
      continue;
    }
    // The ladder (and any other array field) cascades as one unit, and an EMPTY array is
    // "no preference" — it must never shadow a populated scope above it.
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    return { value: value as NonNullable<BotSequenceConfigValues[K]>, source: scope };
  }
  return { value: null, source: "default" };
}

/** Resolve one (location, type)'s effective values from its three scope rows. */
export function resolveBotSequenceConfigValues(rows: BotSequenceConfigScopeRows): ResolvedBotSequenceConfigValues {
  const mode = resolveField(rows, "mode");
  const ladder = resolveField(rows, "ladder");
  const scheduleSlug = resolveField(rows, "scheduleSlug");
  const minGapDays = resolveField(rows, "minGapDays");
  const optIns = resolveField(rows, "pastDueInactiveOptInStatuses");

  // Defaults a location resolves to when no scope expressed a preference:
  // the shared "sequence" schedule, no gap.
  return {
    mode,
    ladder,
    scheduleSlug: { value: scheduleSlug.value ?? BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG, source: scheduleSlug.source },
    minGapDays: { value: minGapDays.value ?? 0, source: minGapDays.source },
    pastDueInactiveOptInStatuses: optIns
  };
}

// ── Operator-facing wording (ENG-3216 Round 2, R7) ─────────────────────────────────────
// These messages reach the SaveBar and the API error unchanged, so they use the page's
// words (Day 3, text message, call, voicemail message, email, note in the FMS, "Send
// immediately", "Always send on this day") and never a storage name.

const LANGUAGE_NAMES: Record<string, string> = { en: "English", es: "Spanish" };
const languageName = (locale: string): string => LANGUAGE_NAMES[locale] ?? locale;
const allowedLanguages = (): string => BOT_SEQUENCE_BODY_LOCALES.map(languageName).join(", ");

/** "the text message" / "the call" — the thing a delivery's wording belongs to. */
function deliveryNoun(channel: string): string {
  return channel === Channel.VOICE ? "the call" : channel === Channel.SMS ? "the text message" : `the ${channel} message`;
}

/**
 * Every `{{ }}` reference in an authored text must parse and must name something that
 * resolves (VarDef key, location link, or fire-time `Sequence:*` variable). Rejected at
 * WRITE time because a required variable with no value fails the send — an unknown key is
 * a guaranteed failure and belongs here, not in the ledger days later.
 */
function findTextVarErrors(text: string, day: string, what: string): string[] {
  const errors = findBraceVarSyntaxErrors(text).map((error) => `${day}: ${what} has a variable that is not written correctly - ${error}.`);
  for (const ref of extractBraceVarRefs(text)) {
    if (!isResolvableVarKey(ref.key)) {
      errors.push(`${day}: ${what} uses a variable that does not exist: ${ref.key}.`);
    }
  }
  return errors;
}

function findBodiesErrors(bodies: Record<string, string> | undefined, day: string, what: string, maxLength: number): string[] {
  const errors: string[] = [];
  const entries = Object.entries(bodies ?? {});
  const allowed: readonly string[] = BOT_SEQUENCE_BODY_LOCALES;
  if (!bodies || !entries.some(([locale]) => locale === BOT_SEQUENCE_DEFAULT_BODY_LOCALE)) {
    errors.push(`${day}: ${what} needs ${languageName(BOT_SEQUENCE_DEFAULT_BODY_LOCALE)} wording (it is used when a tenant's language has none).`);
  }
  for (const [locale, body] of entries) {
    if (!allowed.includes(locale)) {
      errors.push(`${day}: "${locale}" is not a supported language for ${what} (${allowedLanguages()}).`);
    }
    if (typeof body !== "string" || body.trim().length === 0) {
      errors.push(`${day}: ${what}'s ${languageName(locale)} wording is empty.`);
    } else if (body.length > maxLength) {
      errors.push(`${day}: ${what}'s ${languageName(locale)} wording is over ${maxLength} characters.`);
    } else {
      errors.push(...findTextVarErrors(body, day, `${what}'s ${languageName(locale)} wording`));
    }
  }
  return errors;
}

function findTemplateErrors(template: unknown, day: string, what: string): string[] {
  const allowed: readonly unknown[] = BOT_SEQUENCE_CONTENT_TEMPLATES;
  return template === undefined || allowed.includes(template) ? [] : [`${day}: the template for ${what} is invalid.`];
}

function findDeliveryErrors(delivery: BotSequenceConfigStepDelivery, day: string): string[] {
  const errors: string[] = [];
  const channels: readonly string[] = BOT_SEQUENCE_DELIVERY_CHANNELS;
  const what = deliveryNoun(delivery.channel);
  if (!channels.includes(delivery.channel)) {
    errors.push(`${day}: "${delivery.channel}" is not a supported channel - a day can send a text message or a call.`);
  }
  const content = delivery.content;
  if (!content || (content.mode !== "generated" && content.mode !== "verbatim")) {
    errors.push(`${day}: choose "Written by the assistant" or "Exact wording" for ${what}.`);
  } else if (content.mode === "verbatim") {
    errors.push(...findBodiesErrors(content.bodies, day, what, BOT_SEQUENCE_STEP_LIMITS.verbatimBody));
  } else {
    errors.push(...findTemplateErrors(content.template, day, what));
    if (content.guidance !== undefined) {
      if (typeof content.guidance !== "string") {
        errors.push(`${day}: the guidance for ${what} is invalid.`);
      } else if (content.guidance.length > BOT_SEQUENCE_STEP_LIMITS.guidance) {
        errors.push(`${day}: the guidance for ${what} is over ${BOT_SEQUENCE_STEP_LIMITS.guidance} characters.`);
      }
    }
  }
  if (delivery.allowSplit !== undefined && typeof delivery.allowSplit !== "boolean") {
    errors.push(`${day}: the "Allow more than one text segment" setting is invalid.`);
  }
  if (delivery.voicemail !== undefined) {
    if (delivery.channel !== Channel.VOICE) {
      errors.push(`${day}: a voicemail message only applies to a call.`);
    } else if (typeof delivery.voicemail.enabled !== "boolean") {
      errors.push(`${day}: the voicemail on/off setting is invalid.`);
    } else if (delivery.voicemail.enabled) {
      errors.push(...findBodiesErrors(delivery.voicemail.bodies, day, "the voicemail message", BOT_SEQUENCE_STEP_LIMITS.verbatimBody));
    }
  }
  return errors;
}

/** "Day 3" when the day number is usable, else "Day #2" (its position) so the message still points somewhere. */
function dayLabel(step: BotSequenceConfigStep, index: number): string {
  return Number.isInteger(step.offsetDays) && step.offsetDays >= 0 ? `Day ${step.offsetDays}` : `Day #${index + 1}`;
}

function findStepErrors(step: BotSequenceConfigStep, index: number, sequenceType: BotSequenceType): string[] {
  const errors: string[] = [];
  const day = dayLabel(step, index);

  if (typeof step.key !== "string" || step.key.trim().length === 0) {
    errors.push(`${day} is missing its internal id - remove the day and add it again.`);
  }
  if (!Number.isInteger(step.offsetDays) || step.offsetDays < 0) {
    errors.push(`${day}: the day number must be a whole number, 0 or more (got ${JSON.stringify(step.offsetDays)}).`);
  }
  if (typeof step.enabled !== "boolean") {
    errors.push(`${day}: the on/off setting is invalid.`);
  }
  if (step.callScriptSlug !== undefined && (typeof step.callScriptSlug !== "string" || step.callScriptSlug.trim().length === 0)) {
    errors.push(`${day}: the starting script is invalid - pick one from the list or leave it empty.`);
  }

  const emailEnabled = step.email?.enabled === true;
  if (step.deliveries !== undefined) {
    if (!Array.isArray(step.deliveries)) {
      errors.push(`${day}: the message settings are malformed.`);
    } else if (step.deliveries.length === 0 && !emailEnabled) {
      errors.push(`${day} has nothing to send. Turn on a text message, a call, or an email.`);
    } else {
      step.deliveries.forEach((delivery) => errors.push(...findDeliveryErrors(delivery, day)));
      const channels = step.deliveries.map((delivery) => delivery.channel);
      if (new Set(channels).size !== channels.length) {
        errors.push(`${day} lists the same channel twice - keep one text message and one call at most.`);
      }
    }
  }

  if (step.email !== undefined) {
    if (typeof step.email.enabled !== "boolean") {
      errors.push(`${day}: the email on/off setting is invalid.`);
    } else if (step.email.enabled) {
      errors.push(...findTemplateErrors(step.email.template, day, "the email"));
      const subject = step.email.subject;
      const body = step.email.body;
      if (typeof subject !== "string" || subject.trim().length === 0 || subject.length > BOT_SEQUENCE_STEP_LIMITS.emailSubject) {
        errors.push(`${day}: the email needs a subject (up to ${BOT_SEQUENCE_STEP_LIMITS.emailSubject} characters).`);
      } else {
        errors.push(...findTextVarErrors(subject, day, "the email subject"));
      }
      if (typeof body !== "string" || body.trim().length === 0 || body.length > BOT_SEQUENCE_STEP_LIMITS.emailBody) {
        errors.push(`${day}: the email needs a body (up to ${BOT_SEQUENCE_STEP_LIMITS.emailBody} characters).`);
      } else {
        errors.push(...findTextVarErrors(body, day, "the email"));
      }
    }
  }

  if (step.fmsNote !== undefined) {
    if (typeof step.fmsNote.enabled !== "boolean") {
      errors.push(`${day}: the "Note in the FMS" on/off setting is invalid.`);
    } else if (step.fmsNote.enabled) {
      const text = step.fmsNote.text;
      if (typeof text !== "string" || text.trim().length === 0 || text.length > BOT_SEQUENCE_STEP_LIMITS.fmsNoteText) {
        errors.push(`${day}: the note in the FMS needs text (up to ${BOT_SEQUENCE_STEP_LIMITS.fmsNoteText} characters).`);
      } else {
        errors.push(...findTextVarErrors(text, day, "the note in the FMS"));
      }
    }
  }

  if (step.deferrable !== undefined && typeof step.deferrable !== "boolean") {
    errors.push(`${day}: the "Always send on this day" setting is invalid.`);
  }
  if (step.sendASAP !== undefined) {
    if (typeof step.sendASAP !== "boolean") {
      errors.push(`${day}: the "Send immediately" setting is invalid.`);
    } else if (step.sendASAP && sequenceType !== BotSequenceType.MOVE_IN && sequenceType !== BotSequenceType.MOVE_OUT) {
      errors.push(`${day}: "Send immediately" is only offered on Move In and Move Out.`);
    }
  }
  return errors;
}

/**
 * Reject a malformed config at WRITE time with a message an operator can act on, rather
 * than letting it surface days later as "the scheduler booked nothing" (or booked wrong).
 * Returns [] when the values are storable. Pure — the API, the engine and the unit tests
 * share it, and the SaveBar shows the strings verbatim. Semantics TSOA cannot check: day
 * invariants, uniqueness, per-type rules ("Send immediately"), variable references, size
 * ceilings.
 */
export function findBotSequenceConfigErrors(values: BotSequenceConfigValues, sequenceType: BotSequenceType): string[] {
  const errors: string[] = [];

  if (values.ladder != null) {
    if (!Array.isArray(values.ladder) || values.ladder.length === 0) {
      errors.push("Add at least one day, or revert to the inherited days.");
    } else {
      values.ladder.forEach((step, index) => errors.push(...findStepErrors(step, index, sequenceType)));
      const keys = values.ladder.map((step) => step.key);
      if (new Set(keys).size !== keys.length) {
        errors.push("Two days share the same internal id - remove one of them and add it again.");
      }
      const offsets = values.ladder.map((step) => step.offsetDays);
      const duplicateOffset = offsets.find((offset, index) => offsets.indexOf(offset) !== index);
      if (duplicateOffset !== undefined) {
        errors.push(`Day ${duplicateOffset} appears twice.`);
      }
    }
  }
  if (values.scheduleSlug != null && (typeof values.scheduleSlug !== "string" || values.scheduleSlug.trim().length === 0)) {
    errors.push("Send hours must name a schedule group, or be left unset to use Sequence Sending Hours.");
  }
  if (values.minGapDays != null && (!Number.isInteger(values.minGapDays) || values.minGapDays < 0)) {
    errors.push(`Minimum days between messages must be a whole number, 0 or more (got ${JSON.stringify(values.minGapDays)}).`);
  }
  if (values.pastDueInactiveOptInStatuses != null) {
    const bad = values.pastDueInactiveOptInStatuses.filter((status) => typeof status !== "string" || status.trim().length === 0);
    if (bad.length > 0) {
      errors.push("Inactive lease statuses cannot be blank.");
    }
  }
  return errors;
}
